import { Injectable, signal } from '@angular/core';

import { CoverageAudioChannel, CoverageDevice } from '@shure/shared/angular/features/device-coverage/data-access';

import { OnboardingStep, OnboardingAction } from '../models';

@Injectable({
	providedIn: 'root'
})
export class Coverage3DOnboardingService {
	private readonly onboardingDevice = signal<CoverageDevice | undefined>(undefined);

	public readonly $onboardingDevice = this.onboardingDevice.asReadonly();

	public readonly onboardingAction = signal<OnboardingAction | undefined>(undefined);

	public readonly activeStep = signal<OnboardingStep | undefined>(undefined);

	/** True while the onboarding dialog is open. */
	public readonly isActive = signal(false);

	public setOnboardingDevice(device: CoverageDevice | undefined): void {
		this.onboardingDevice.set(device);
	}

	/** Enables the next disabled channel on the onboarding device; it is never sent to the device API. */
	public addChannel(): void {
		const channelToEnable = this.onboardingDevice()?.features.audioChannels?.value?.find(
			(channel) => !channel.enabled
		);
		if (!channelToEnable) {
			return;
		}

		this.updateChannels((channel) => (channel.id === channelToEnable.id ? { ...channel, enabled: true } : channel));

		if (this.activeStep() === OnboardingStep.LobeAdd) {
			this.onboardingAction.set(OnboardingAction.LobeAdd);
		}
	}

	public setChannelRotation(updates: { channelId: string; rotation: number }[]): void {
		this.updateChannels((channel) => {
			const update = updates.find(({ channelId }) => channelId === channel.id);
			if (!update) {
				return channel;
			}
			return {
				...channel,
				features: { ...channel.features, micAngle: { value: update.rotation, isMissing: false } }
			};
		});

		if (this.activeStep() === OnboardingStep.LobeClickAndDrag) {
			this.onboardingAction.set(OnboardingAction.LobeClickAndDrag);
		}
	}

	/** Reports the multi-select action when at least two lobes are selected during the lobe multi-select step. */
	public selectChannels(channelIds: string[]): void {
		if (this.activeStep() === OnboardingStep.LobeMultiSelect && channelIds.length >= 2) {
			this.onboardingAction.set(OnboardingAction.LobeMultiSelect);
		}
	}

	public resetOnboardingState(): void {
		this.isActive.set(false);
		this.onboardingDevice.set(undefined);
		this.onboardingAction.set(undefined);
		this.activeStep.set(undefined);
	}

	private updateChannels(mapChannel: (channel: CoverageAudioChannel) => CoverageAudioChannel): void {
		const device = this.onboardingDevice();
		const audioChannels = device?.features.audioChannels;
		if (!device || !audioChannels?.value) {
			return;
		}
		this.onboardingDevice.set({
			...device,
			features: {
				...device.features,
				audioChannels: { ...audioChannels, value: audioChannels.value.map(mapChannel) }
			}
		});
	}
}
